import { useEffect, useState } from "react";
import ContactApiService from "../services/ContactApiService";
import Groups from "../models/Groups";
import { errorMessage } from "../shared/utils/validators";
import { Alert, Avatar, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, IconButton, Menu, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { AddIcon, DeleteIcon, EditIcon, PersonIcon, SearchIcon } from "evergreen-ui";
import { MoreVert } from "@mui/icons-material";
import CreateGroupDialog from "../components/CreateGroupDialog";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";

function GroupView() {
    const [rows, setRows] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [searchKey, setSearchKey] = useState("");
    const [activePage, setActivePage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeGroup, setActiveGroup] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
      });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState("single");
    const [groupToDelete, setGroupToDelete] = useState(null);

 function fetch() {
    ContactApiService.getGroupList({
        page: activePage,
        limit: limit,
        sortBy: sortBy,
        sortOrder: sortOrder,
    }).then((res) => {
      setRows(res);
      if (selectedGroup) {
      const group = res.find((cont) => cont.getId() === selectedGroup.getId());
      setSelectedGroup(group);
    }
    
    });
  }

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    fetch();
  }, [searchKey, sortBy, sortOrder]);

  const filteredGroups = rows.filter((group) => {
  if (!searchKey) return true;

  const name = group.attrs?.name?.toLowerCase() || "";
  const description = group.attrs?.description?.toLowerCase() || "";
  const phone = group.attrs?.phoneNumber || ""; 

  return (
    name.includes(searchKey.toLowerCase()) ||
    description.includes(searchKey.toLowerCase()) ||
    phone.includes(searchKey)
  );
});

  function handleContactRowClick(group) {
    setSelectedGroup(group);
  };

  function handleMoreOptionsClick(event, group) {
    setAnchorEl(event.currentTarget);
    setActiveGroup(group);
  };

  function handleMoreOptionsClose() {
    setAnchorEl(null);
    setActiveGroup(null);
  };

  function handleEditGroup() {
    setEditingGroup(activeGroup);
    setCreateDialogOpen(true);
    handleMoreOptionsClose();
  };

  function handleAddGroup() {
    setEditingGroup(null);
    setCreateDialogOpen(true);
  };

  function handleCancelDelete() {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
  };

  function handleConfirmDelete() {
    (async () => {
      try {
        if (deleteType === "single" && groupToDelete?.attrs?.id) {
          await ContactApiService.deleteGroup(groupToDelete.attrs.id);
        } else if (selectedGroups.length > 0) {
          await Promise.all(
            selectedGroups.map((group) =>
              ContactApiService.deleteGroup(group.attrs.id)
            )
          );
        }
        setSnackbar({
          open: true,
          message: "Group deleted successfully",
          severity: "success",
        });
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
        setSelectedGroups([]);
        setSelectedGroup(null);
        fetch();
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to delete group",
          severity: "error",
        });
      }
    })();
  }

  function handleSearchChange(e) {
    const query = e.target.value;
    setSearchKey(query);
  }

  function handleDeleteSelected() {
    if (selectedGroups.length === 0) return;
    setDeleteType("multiple");
    setDeleteDialogOpen(true);
  }
  function handleSelectAll(e) {
    if (e.target.checked) {
       const updatedRows = rows.map(g => ({
    ...g,
    isSelected: true,
  }));

  setRows(updatedRows);
      setSelectedGroups(updatedRows)
    } else {
       const updatedRows = rows.map(g => ({
    ...g,
    isSelected: false,
  }));

  setRows(updatedRows);
      setSelectedGroups([]);
    }
  }

  function handleGroupSelection(id) {
    if (id) {
        setRows(prevRows =>
    prevRows.map(row =>
      row.attrs.id === id
        ? { ...row, isSelected: true }
        : { ...row, isSelected: false }
    )
  );
    }
  }

  function handleDeleteGroup() {
    if (!activeGroup) return;
    setDeleteType("single");
    setGroupToDelete(activeGroup);
    setDeleteDialogOpen(true);
    handleMoreOptionsClose();
  }

  async function handleSaveGroup(contactData) {
      const model = new Groups();
      model.setName(contactData.name);
      model.setDescription(contactData.description);
      model.setContactsList(contactData.contactsList?.map((c) => c.attrs));
      if (editingGroup) {
        try {
        model.setId(editingGroup.getId());
        await ContactApiService.updateGroup(
          model
        );
          setSnackbar({
            open: true,
            message: "Group updated successfully",
            severity: "success",
          });
        } catch (error) {
          const message = errorMessage(error);
          setSnackbar({
            open: true,
            message: message || "Failed to edit group",
            severity: "error",
          });
        }
      } else {
        try {
        await ContactApiService.createGroup(model);
          setSnackbar({
            open: true,
            message:  "Group added successfully",
            severity: "success",
          });
        } catch (error) {
          const message = errorMessage(error);
          setSnackbar({
            open: true,
            message: message || "Failed to add group",
            severity: "error",
          });
      }
      }
      fetch();
    };
    const visibleSelectedCount = rows?.filter(
    (contact) => contact.isSelected
  ).length;
  const isAllSelected =
    rows.length > 0 && visibleSelectedCount === rows.length;
  const isIndeterminate =
    visibleSelectedCount > 0 && visibleSelectedCount < rows.length;

    return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
          }}
        >
          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Top Bar */}
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                width: "100%",
                // borderBottom: "1px solid #e8e8e8",
                backgroundColor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              {/* Single Row with All Components */}
                {rows.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {/* Left Section - Search */}
                <TextField
                  placeholder="Search by name or number"
                  value={searchKey}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }}
                      />
                    ),
                  }}
                  sx={{
                    width: 320,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      backgroundColor: "#fafafa",
                      border: "1px solid #e0e0e0",
                      fontSize: "0.875rem",
                      gap: 3,
                      fontFamily: "Poppins, sans-serif",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "#d0d0d0",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        borderColor: "#1976d2",
                        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
    
                {/* Right Section - Filter and Add Button */}
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  {/* Add Contact Button */}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    onClick={handleAddGroup}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      px: 2,
                      py: 0.75,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      background:
                        "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                      boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
                      border: "1px solid rgba(25, 118, 210, 0.1)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                        boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                        transform: "translateY(-1px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Add Group
                  </Button>
                </Box>
              </Box>
              )}
              {/* Bulk Actions - Show below the main row when contacts are selected */}
              {selectedGroups.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedGroups.length} Group(s) selected
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDeleteSelected}
                    sx={{ borderRadius: 2 }}
                  >
                    Delete Selected
                  </Button>
                </Box>
              )}
            </Paper>
    {/* ===== EMPTY GROUPS PLACEHOLDER ===== */}
{rows.length === 0 && (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      textAlign: "center",
      color: "#9ca3af",
      py: 6,
    }}
  >
    <GroupOutlinedIcon sx={{ fontSize: 72, mb: 1.5 }} />

    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 600,
        mb: 0.5,
        fontFamily: "Poppins, sans-serif",
        color: "#374151",
      }}
    >
      No groups available
    </Typography>

    <Typography
      sx={{
        fontSize: 14,
        maxWidth: 300,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      Create a group to start managing contacts together
    </Typography>
         {/* Add Contact Button */}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    onClick={handleAddGroup}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      px: 2,
                      py: 0.75,
                      mt: 3,
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      background:
                        "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                      boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
                      border: "1px solid rgba(25, 118, 210, 0.1)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                        boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                        transform: "translateY(-1px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Add Group
                  </Button>
  </Box>
)}
            {/* Table */}
            {filteredGroups.length > 0 && (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                flex: 1,
                width: "100%",
                borderRadius: 0,
                "& .MuiTable-root": {
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  width: "100%",
                },
              }}
            >
              <Table stickyHeader sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        // borderBottom: "2px solid #e0e0e0",
                        fontWeight: 600,
                      }}
                    >
                      {/* <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        sx={{ color: "#666" }}
                      /> */}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        // borderBottom: "2px solid #e0e0e0",
                        fontWeight: 600,
                        fontFamily: "Poppins, sans-serif",
                        color: "#333",
                      }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        // borderBottom: "2px solid #e0e0e0",
                        fontWeight: 600,
                        fontFamily: "Poppins, sans-serif",  
                        color: "#333",
                      }}
                    >
                      Contacts Count
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        // borderBottom: "2px solid #e0e0e0",
                        fontWeight: 600,
                        fontFamily: "Poppins, sans-serif",
                        color: "#333",
                      }}
                    >
                      Created At
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        // borderBottom: "2px solid #e0e0e0",
                        fontWeight: 600,
                        fontFamily: "Poppins, sans-serif",
                        color: "#333",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.attrs.id}
                      onClick={() => handleContactRowClick(row)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                        backgroundColor:
                          selectedGroup?.getId?.() === row.attrs.id
                            ? "#e3f2fd"
                            : "transparent",
                      }}
                    >
                      <TableCell padding="checkbox">
                        {/* <Checkbox
                          checked={row.isSelected}
                          onChange={() => {
                            setSelectedGroup(row);
                            handleGroupSelection(row.attrs.id);
                        }}
                          sx={{ color: "#666" }}
                        /> */}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: "#1976d2",
                              fontSize: "0.875rem",
                            }}
                          >
                            {row.avatar ? (
                              <img src={row.avatar} alt={row.attrs.name} />
                            ) : (
                              <PersonIcon />
                            )}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: "Poppins, sans-serif"}}>
                              {row.attrs.name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontFamily: "Poppins, sans-serif" }}>{row.attrs.contactsCount}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontFamily: "Poppins, sans-serif" }}>{row.attrs.createdAt}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(event) =>
                            handleMoreOptionsClick(event, row)
                          }
                          sx={{ color: "#666" }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            {/* More Options Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMoreOptionsClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                  minWidth: 150,
                },
              }}
            >
              <MenuItem onClick={handleEditGroup} sx={{ gap: 1 }}>
                <EditIcon fontSize="small" />
                Edit
              </MenuItem>
              <MenuItem
                onClick={handleDeleteGroup}
                sx={{ gap: 1, color: "#d32f2f" }}
              >
                <DeleteIcon fontSize="small" />
                Delete
              </MenuItem>
            </Menu>
    
            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={handleCancelDelete}
              aria-labelledby="delete-dialog-title"
              aria-describedby="delete-dialog-description"
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  minWidth: 400,
                },
              }}
            >
              <DialogTitle id="delete-dialog-title" sx={{ pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#d32f2f" }}>
                  Confirm Delete
                </Typography>
              </DialogTitle>
              <DialogContent sx={{ pb: 2 }}>
                <DialogContentText id="delete-dialog-description">
                  {deleteType === "single" && groupToDelete ? (
                    <>
                      Are you sure you want to delete{" "}
                      <strong>{groupToDelete.attrs?.name || "this group"}</strong>?
                      <br />
                      <br />
                      This action cannot be undone.
                    </>
                  ) : (
                    <>
                      Are you sure you want to delete{" "}
                      <strong>{selectedGroups.length} selected group(s)</strong>
                      ?
                      <br />
                      <br />
                      This action cannot be undone.
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button
                  onClick={handleCancelDelete}
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  variant="contained"
                  color="error"
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    px: 3,
                    background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)",
                    },
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
            <CreateGroupDialog
            open={createDialogOpen}
            onClose={() => {setCreateDialogOpen(false); setEditingGroup(null)}}
            onSave={handleSaveGroup}
            editGroup={editingGroup}
            />
    
            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={3000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                sx={{ borderRadius: 2 }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Box>
        </Box>
      );

}
export default GroupView;